import { useContext } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  Text,
  VStack,
  Box,
  HStack,
  Divider,
  useToast,
  Link,
  Badge,
} from '@chakra-ui/react';
import { DownloadIcon } from '@chakra-ui/icons';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiAcknowledgeNotification } from 'api';
import type { Notification } from 'types/api.types';
import { AuthContext } from 'contexts/AuthContext';
import { ROLE_ADMIN } from 'variables/constants';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  notification: Notification | null;
}

export const NotificationDetailModal = ({ isOpen, onClose, notification }: Props) => {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { user } = useContext(AuthContext);
  
  // Check if user is admin or corporate leadership
  const canSeeTargetAudience = user.role === ROLE_ADMIN || notification?.target_corporate_leadership;

  const acknowledgeMutation = useMutation({
    mutationFn: apiAcknowledgeNotification,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      toast({
        title: 'Notification acknowledged',
        status: 'success',
        duration: 2000,
      });
      onClose();
    },
  });

  const handleAcknowledge = () => {
    if (notification) {
      acknowledgeMutation.mutate(notification.id);
    }
  };

  if (!notification) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="xl">
      <ModalOverlay />
      <ModalContent maxH="80vh" display="flex" flexDirection="column">
        <ModalHeader>
          <VStack align="start" spacing={2}>
            <Text fontSize="xl" fontWeight="bold" wordBreak="break-word">
              {notification.title}
            </Text>
            <HStack spacing={2}>
              {notification.is_edited && (
                <Badge colorScheme="blue">Edited</Badge>
              )}
              {notification.requires_acknowledgement && (
                <Badge colorScheme="red">Mandatory</Badge>
              )}
            </HStack>
          </VStack>
        </ModalHeader>
        <ModalCloseButton />
        
        <Divider />
        
        <ModalBody overflow="auto" flex="1" py={6}>
          <VStack align="start" spacing={4}>
            {notification.summary && (
              <Box
                w="full"
                p={4}
                bg="gray.50"
                borderRadius="md"
                borderLeftWidth="4px"
                borderLeftColor="blue.500"
              >
                <Text fontWeight="semibold" color="gray.700" fontSize="md" mb={2}>
                  Summary
                </Text>
                <Text fontSize="md" color="gray.600">
                  {notification.summary}
                </Text>
              </Box>
            )}

            <Divider />

            <Box w="full">
              <Text fontWeight="semibold" fontSize="md" mb={2} color="gray.700">
                Details
              </Text>
              <Box
                w="full"
                dangerouslySetInnerHTML={{ __html: notification.content }}
                sx={{
                  '& img': {
                    maxWidth: '100%',
                    height: 'auto',
                    borderRadius: 'md',
                    my: 2,
                  },
                  '& a': {
                    color: 'blue.500',
                    textDecoration: 'underline',
                  },
                  '& h1, & h2, & h3': {
                    fontWeight: 'bold',
                    my: 2,
                  },
                  '& ul, & ol': {
                    pl: 6,
                  },
                }}
              />
            </Box>
            
            {/* Attachments Section */}
            {notification.attachments && notification.attachments.length > 0 && (
              <>
                <Divider />
                <Box w="full">
                  <Text fontWeight="semibold" mb={2}>Attachments</Text>
                  <VStack align="stretch" spacing={2}>
                    {notification.attachments.map((attachment) => (
                      <HStack
                        key={attachment.id}
                        p={3}
                        borderWidth="1px"
                        borderRadius="md"
                        justify="space-between"
                      >
                        <HStack>
                          <DownloadIcon />
                          <VStack align="start" spacing={0}>
                            <Text fontSize="sm" fontWeight="medium">
                              {attachment.filename}
                            </Text>
                            <Text fontSize="xs" color="gray.500">
                              {(attachment.file_size / 1024).toFixed(2)} KB
                            </Text>
                          </VStack>
                        </HStack>
                        <Link href={attachment.file} isExternal>
                          <Button size="sm" colorScheme="blue" variant="outline">
                            Download
                          </Button>
                        </Link>
                      </HStack>
                    ))}
                  </VStack>
                </Box>
              </>
            )}

            {/* Target Audience Section - Only visible to admins and corporate leadership */}
            {canSeeTargetAudience &&
              (notification.target_provinces_display?.length > 0 ||
                notification.target_roles_display?.length > 0 ||
                notification.target_corporate_leadership) && (
                <>
                  <Divider />
                  <Box w="full">
                    <Text fontWeight="semibold" mb={2}>Target Audience</Text>
                    <VStack align="start" spacing={2}>
                      {notification.target_provinces_display && 
                        notification.target_provinces_display.length > 0 && (
                        <HStack spacing={2} flexWrap="wrap">
                          <Text fontSize="sm" fontWeight="medium" color="gray.600">
                            Provinces:
                          </Text>
                          {notification.target_provinces_display.map((p) => (
                            <Badge key={p.id} colorScheme="green">
                              {p.code}
                            </Badge>
                          ))}
                        </HStack>
                      )}
                      {notification.target_roles_display && 
                        notification.target_roles_display.length > 0 && (
                        <HStack spacing={2} flexWrap="wrap">
                          <Text fontSize="sm" fontWeight="medium" color="gray.600">
                            Roles:
                          </Text>
                          {notification.target_roles_display.map((r) => (
                            <Badge key={r.id} colorScheme="orange">
                              {r.name}
                            </Badge>
                          ))}
                        </HStack>
                      )}
                      {notification.target_corporate_leadership && (
                        <HStack spacing={2}>
                          <Text fontSize="sm" fontWeight="medium" color="gray.600">
                            Corporate:
                          </Text>
                          <Badge colorScheme="purple">
                            Corporate Leadership
                          </Badge>
                        </HStack>
                      )}
                    </VStack>
                  </Box>
                </>
              )}
          </VStack>
        </ModalBody>
        
        <Divider />
        
        <ModalFooter>
          <HStack w="full">
            {notification.requires_acknowledgement ? (
              // Only show "I Acknowledge" button if not already acknowledged
              notification.status !== 'acknowledged' ? (
                <Button
                  colorScheme="red"
                  onClick={handleAcknowledge}
                  isLoading={acknowledgeMutation.isPending}
                  w="full"
                  size="lg"
                >
                  I Acknowledge
                </Button>
              ) : (
                <Button
                  variant="ghost"
                  onClick={onClose}
                  w="full"
                >
                  Close
                </Button>
              )
            ) : (
              <Button variant="ghost" onClick={onClose} w="full">
                Close
              </Button>
            )}
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
